import type { RequestInitiator } from '@ms/yammer-telemetry-support';

export type MimeBinary = 'application/octet-stream';
export const MimeBinary = 'application/octet-stream';
export type MimeForm = 'application/x-www-form-urlencoded';
export const MimeForm = 'application/x-www-form-urlencoded';
export type MimeJson = 'application/json';
export const MimeJson = 'application/json';
export type MimeText = 'text/plain';
export const MimeText = 'text/plain';
export type MimeFormData = 'multipart/form-data';
export const MimeFormData = 'multipart/form-data';
export type MimeImage = 'image/';
export const MimeImage = 'image/';
export type MimeVideo = 'video/';
export const MimeVideo = 'video/';
export type MimePdf = 'application/pdf';
export const MimePdf = 'application/pdf';
export type MimeAny = '*/*';
export const MimeAny = '*/*';

export type ApiMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type ApiMiddleware = (request: ManagedApiRequest) => Promise<ApiRequest>;

export interface ManagedApiRequest extends ApiRequest {
  readonly resource?: string;
  readonly scopes?: string[];
}
export type ApiQuery = Readonly<Record<string, unknown>>;

export type ApiHeaders = Record<string, string>;

export type ApiRequestType =
  | 'Ecs'
  | 'Graphql'
  | 'LegacyYammer'
  | 'Ocv'
  | 'SharePoint'
  | 'Substrate'
  | 'ThirdParty'
  | 'O365Shell';

export interface ApiRequest {
  readonly path: string;
  readonly host?: string;
  readonly query?: ApiQuery;
  readonly method?: ApiMethod;
  readonly mode?: RequestMode;
  readonly body?: object | string;
  readonly contentType?: MimeForm | MimeJson | MimeText | MimeFormData;
  readonly responseType?: MimeBinary | MimeForm | MimeJson | MimeText | MimeAny;
  readonly headers?: ApiHeaders;
  readonly apiName?: string;
  readonly requestType: ApiRequestType;
  readonly credentials?: RequestCredentials;
  readonly initiator?: RequestInitiator;
}

export type ExecuteRequest<TResponse> = (request: ApiRequest) => Promise<ApiResponse<TResponse>>;

export interface ApiResponse<TResponse> {
  readonly body: TResponse;
  readonly headers: Headers;
}

export interface GraphqlOperation {
  readonly operationName: string;
  readonly variables?: Record<string, Record<string, unknown>>;
}

export interface GraphqlError {
  readonly message?: string;
  readonly path?: string[];
  readonly type?: string;
  readonly correlationId?: string;
  readonly locations?: [{ readonly column: number; readonly line: number }];
  readonly extensions?: Record<string, string>;
}

export type GraphqlErrorReason =
  | 'ACCESS_FORBIDDEN'
  | 'ADDING_FOREIGN_USER_BLOCKED_IN_POSTERS_NETWORK_ERROR'
  | 'ALREADY_EXISTS'
  | 'BODY_BLANK'
  | 'BODY_TOO_LONG'
  | 'CALENDAR_INVITE_ALREADY_EXISTS'
  | 'CALLER_NOT_ADMIN'
  | 'CALLER_NOT_ORGANIZER'
  | 'CANNOT_CANCEL_LIVE_EVENT'
  | 'CANNOT_INVITE_THIS_RECIPIENT_TO_GROUP'
  | 'CANNOT_REVOKE_LAST_ADMIN'
  | 'CANNOT_UPDATE_FINISHED_EVENT'
  | 'CANNOT_UPDATE_START_TIME_IN_LIVE_STATE'
  | 'COMMUNITY_AMA_COORGANIZER_NOT_MEMBER'
  | 'CONFLICTING_CAMPAIGN_HASHTAG'
  | 'CONFLICTING_GROUP'
  | 'CONFLICTING_NETWORK'
  | 'CONFLICTING_PRIVACY_LEVELS'
  | 'CONFLICTING_REPLY_TO'
  | 'CONFLICTING_THREAD'
  | 'DIRECT_MESSAGE_FILE_UPLOAD_FORBIDDEN'
  | 'DISPLAY_NAME_ALREADY_EXISTS'
  | 'END_TIME_LESS_THAN_START_TIME'
  | 'ENTITY_CREATION_LIMIT_EXCEEDED'
  | 'EVENT_DESCRIPTION_LONGER_THAN_4000'
  | 'EVENT_DURATION_MORE_THAN_4_HOURS'
  | 'EVENT_TITLE_LONGER_THAN_1000'
  | 'EXTERNAL_FORBIDDEN_ON_CURRENT_USER_NETWORK'
  | 'EXTERNAL_FORBIDDEN_ON_FOREIGN_USER_NETWORK'
  | 'EXTERNAL_NOT_ALLOWED'
  | 'FAVORITE_LIMIT_REACHED'
  | 'FILE_COUNT_EXCEEDED'
  | 'FILE_STORAGE_QUOTA_LIMIT_REACHED'
  | 'FINAL_ADMIN_WARNING'
  | 'FOLLOWED_LIMIT_REACHED'
  | 'GREATER_THAN_HUNDRED_POLL_OPTIONS'
  | 'GROUP_CREATION_FORBIDDEN'
  | 'GROUP_DELETED'
  | 'GROUP_DOES_NOT_EXIST'
  | 'GROUP_INVITE_NOT_ALLOWED'
  | 'GROUP_NOT_PRESENT'
  | 'INCORRECT_MESSAGE_TYPE'
  | 'INSIGHTS_DATA_NOT_FOUND'
  | 'INSIGHTS_PRIVACY_THRESHOLD_NOT_MET'
  | 'INSUFFICIENT_CAMPAIGN_PRIVILEGE'
  | 'INSUFFICIENT_PRIVILEGE_TO_ADD_GUEST'
  | 'INVALID_BODY_FORMAT'
  | 'INVALID_CONTENT_STATE'
  | 'INVALID_DESCRIPTION'
  | 'INVALID_DISPLAY_NAME'
  | 'INVALID_EMAIL_ADDRESS'
  | 'INVALID_FILE_UPLOAD_REQUEST'
  | 'INVALID_ID_FORMAT'
  | 'INVALID_INVITED_USERS'
  | 'INVALID_PRAISE'
  | 'INVALID_REPLY_REFERENCED'
  | 'INVALID_STATE_TRANSITION'
  | 'INVALID_TOPIC'
  | 'INVALID_URL_SCHEME_IN_BODY'
  | 'INVITED_USERS_OVER_LIMIT'
  | 'JSON_INVALID'
  | 'LIMITED_CAPABILITIES'
  | 'MEDIA_FILE_FORMAT_NOT_SUPPORTED'
  | 'MESSAGE_DELETED'
  | 'MISSING_GROUP'
  | 'MISSING_TENANT_ID'
  | 'MISSING_USER_LOCALE'
  | 'MOVE_THREAD_NOT_ALLOWED_TO_GROUP'
  | 'NETWORK_BLANK'
  | 'NETWORK_NOT_PRESENT'
  | 'NO_ACCESS_TO_GROUP'
  | 'NO_PERMISSION_TO_ANNOUNCE'
  | 'NO_PERMISSION_TO_CREATE_QUESTION'
  | 'NO_YAMMER_LICENSE'
  | 'NONE'
  | 'NOT_AN_ADMIN'
  | 'NOT_AUTHORIZED_TO_ACCESS_RECORDING'
  | 'NOT_ENOUGH_POLL_OPTIONS'
  | 'NOT_FOUND'
  | 'ON_BEHALF_OF_OPERATION_ACCESS_DENIED'
  | 'ON_BEHALF_OF_OPERATION_NOT_ALLOWED'
  | 'ONEDRIVE_FORBIDDEN'
  | 'ONE_DRIVE_NOT_PROVISIONED'
  | 'ONE_DRIVE_STORAGE_QUOTA_LIMIT_REACHED'
  | 'PARENT_NETWORK_CANNOT_GENERATE_TOKEN'
  | 'PENDING_USER'
  | 'PERSISTED_QUERY_NOT_FOUND'
  | 'PIN_LIMIT_REACHED'
  | 'POLL_OPTION_ABOVE_CHAR_LIMIT'
  | 'POLL_OPTIONS_NOT_AN_ARRAY'
  | 'POST_BLOCKED_BY_MODERATION'
  | 'PRESENTER_IS_ALSO_PRODUCER'
  | 'PRESENTER_OUTSIDE_OF_PRIVATE_GROUP'
  | 'PRINCIPAL_NOT_FOUND'
  | 'PRODUCER_OUTSIDE_OF_PRIVATE_GROUP'
  | 'PRODUCER_REQUIRED'
  | 'PUBLISH_MESSAGE_PARTIAL_ERROR'
  | 'RECIPIENT_OUTSIDE_OF_NETWORK'
  | 'RECORDING_NOT_FOUND'
  | 'REPLY_TO_FLEXTERNAL_THREAD_BLOCKED_IN_POSTERS_NETWORK_ERROR'
  | 'REPLY_TO_FLEXTERNAL_THREAD_BLOCKED_IN_RECEIVERS_NETWORK_ERROR'
  | 'RESTRICTED_POSTING_IN_EVENT'
  | 'RESTRICTED_POSTING_IN_GROUP'
  | 'SENDER_BLANK'
  | 'SENDER_CREATOR_MISMATCH'
  | 'SENDER_NOT_ACTIVE'
  | 'SENDER_NOT_PARTICIPANT'
  | 'SENDER_NOT_PRESENT'
  | 'SPAM_DETECTED'
  | 'START_TIME_IN_PAST'
  | 'THREAD_IS_PINNED'
  | 'THREAD_NOT_IN_MODERATION_STATE'
  | 'THREAD_REPLY_DISABLED'
  | 'TITLE_NOT_ALLOWED'
  | 'TOKEN_CANNOT_BE_REDEEMED'
  | 'TOPIC_DISPLAY_NAME_EXCLUDED'
  | 'TOPIC_TO_PRIVATE_MESSAGE'
  | 'UNATTACHABLE'
  | 'UNKNOWN'
  | 'UPDATE_EVENT_NOT_ALLOWED_IN_CURRENT_STATE'
  | 'UPLOAD_INVALID_FILENAME'
  | 'UPLOAD_NO_GROUP_ACCESS'
  | 'USER_IS_NOT_ORGANIZER'
  | 'USER_IS_NULL'
  | 'USER_NOT_IN_EXPERIMENT'
  | 'USER_NOT_MAPPED'
  | 'USER_NOT_PRESENT_IN_GROUP'
  | 'VIDEO_UPLOAD_MAX_DURATION_EXCEEDED';
